import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiChip,
  MiDetailPageHeader,
  MiIcon,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  HTTP_STATUSES,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import {
  mdiAlertOutline,
  mdiChevronLeft,
  mdiPencilOutline,
  mdiPlus,
  mdiPlusMinus,
} from "@mdi/js";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import { AddUpdateMilestoneModal } from "../BuildTemplateDetails/AddUpdateMilestoneModal";
import { useManageMilestonesViewModel } from "../useManageMilestonesViewModel";
import { ManageOrderTemplatesModal } from "./ManageOrderTemplatesModal";

export function ManageMilestones() {
  const { hasPermission } = usePermissions();

  const {
    milestones,
    orderTemplates,
    stateManager,
    openAddModal,
    selectedItem,
    openDeleteModal,
    openAddOrderTemplateModal,
    getMilestones,
    getOrderTemplates,
    updateMilestone,
    deleteMilestone,
    createMilestone,
    setOpenAddModal,
    setSelectedItem,
    setOpenDeleteModal,
    setOpenAddOrderTemplateModal,
  } = useManageMilestonesViewModel();

  const hasEditPermission = hasPermission(PERMISSIONS.MILESTONE.UPDATE);
  const canDelete = hasPermission(PERMISSIONS.MILESTONE.DELETE);
  const canCreate = hasPermission(PERMISSIONS.MILESTONE.CREATE);

  const { classes } = useStyles();
  const theme = useTheme();

  const listFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      groupable: false,
      minWidth: 200,
      flex: 0.8,
    },
    {
      field: "orderTemplates",
      headerName: ENTITY_NAMES.ORDER_TEMPLATES,
      minWidth: 200,
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (p) => (
        <Box display="flex" flexDirection="row" gap={1} flexWrap="wrap">
          {p.row.orderTemplates?.map((orderTemplate) => (
            <MiChip
              key={orderTemplate.id}
              label={orderTemplate.name}
              className={classes.chiplabel}
            />
          ))}
        </Box>
      ),
    },
    {
      field: "abbreviation",
      headerName: "Abbreviation",
      minWidth: 120,
      flex: 0.5,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      minWidth: 180,
      flex: 0.7,
      renderCell: (p) => p.row.createdBy?.trim() || "N/A",
    },
    {
      field: "createdOn",
      headerName: "Created Date",
      renderCell: (p) => formatDate(p.row.createdOn, "DD MMMM YYYY"),
      minWidth: 180,
      flex: 0.7,
    },
    {
      field: "modifiedOn",
      headerName: "Last Updated",
      renderCell: (p) => formatDate(p.row.modifiedOn, "DD MMMM YYYY"),
      minWidth: 180,
      flex: 0.7,
    },
  ];

  const onModalClose = () => {
    setOpenAddModal(false);
    setOpenAddOrderTemplateModal(false);
    setSelectedItem(null);
  };

  const onCreate = async (data) => {
    onModalClose();
    const response = await createMilestone(data);
    if (response.status === HTTP_STATUSES.OK) {
      getMilestones();
    }
  };

  const onUpdate = async (data) => {
    onModalClose();
    const response = await updateMilestone(selectedItem.id, {
      ...data,
      modifiedOn: selectedItem.modifiedOn,
    });
    if (response.status === HTTP_STATUSES.OK) {
      getMilestones();
    }
  };
  const onDelete = async () => {
    const response = await deleteMilestone(selectedItem.id, {
      modifiedOn: selectedItem.modifiedOn,
      name: selectedItem.name,
    });

    if (response.status === HTTP_STATUSES.OK) {
      getMilestones();
      onDeleteCancel();
    }
  };

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.EDIT,
      onClick: () => {
        setSelectedItem(row);
        setOpenAddModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdiPencilOutline}
          size={1}
          color={theme.palette.green.main}
        />
      ),
      disabled: !hasEditPermission,
    },
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setOpenDeleteModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          size={1}
          color={theme.palette.red.main}
        />
      ),
      popperContent: row.isInUse ? (
        <Box className={classes.popper}>
          <MiIcon
            path={mdiAlertOutline}
            color={theme.palette.orange.main}
            size={2}
          />
          <Typography variant="body2">
            This Milestone is in use and cannot be deleted.
          </Typography>
        </Box>
      ) : null,
      disabled: !canDelete || row.isInUse,
    },
    {
      text: ENTITY_NAMES.ORDER_TEMPLATES,
      onClick: () => {
        setSelectedItem(row);
        setOpenAddOrderTemplateModal(true);
      },
      renderIcon: () => <MiIcon path={mdiPlusMinus} size={1} />,
      disabled: !hasEditPermission,
    },
  ];
  const onDeleteCancel = () => {
    setOpenDeleteModal(false);
    setSelectedItem(null);
  };

  useEffect(() => {
    getMilestones();
    getOrderTemplates();
  }, []);

  return (
    <>
      <MiDetailPageHeader
        title="Milestones"
        titleIcon={mdiChevronLeft}
        loading={stateManager.isBusy()}
        actionComponent={
          canCreate && (
            <MiButton
              title="Add Milestone"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{
                path: mdiPlus,
                iconSize: 0.8,
              }}
              data-testid="add-milestone-btn"
              onClick={() => setOpenAddModal(true)}
            />
          )
        }
      />
      <MiBox height="100%" overflow="auto">
        <MiList
          columns={listFieldset}
          data={milestones}
          paginationMode={PAGINATION.CLIENT}
          loading={stateManager.isBusy()}
          getRowActions={getRowActions}
          autoRowHeight
          disableRowSelectionOnClick
          data-testid="milestones-table"
        />
      </MiBox>
      <AddUpdateMilestoneModal
        open={openAddModal}
        onModalClose={onModalClose}
        onSubmit={selectedItem ? onUpdate : onCreate}
        loading={stateManager.isBusy()}
        selectedData={selectedItem}
      />
      <ManageOrderTemplatesModal
        open={openAddOrderTemplateModal}
        orderTemplates={orderTemplates}
        onModalClose={onModalClose}
        onSubmit={onUpdate}
        loading={stateManager.isBusy()}
        selectedData={selectedItem}
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        description={`Are you sure you want to delete the Milestone: ${selectedItem?.name}?`}
        open={openDeleteModal}
        handlePositive={onDelete}
        handleNegative={onDeleteCancel}
        textPositive={BUTTON_LABELS.DELETE}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  popper: {
    padding: theme.spacing(2),
    maxWidth: 240,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.orange.light,
    border: `1px solid ${theme.palette.orange.dark}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  chiplabel: {
    borderRadius: 3,
    height: 20,
    fontWeight: theme.fontWeight.medium,
    fontSize: theme.fontSize.xsmall,
    color: theme.palette.bluegrey.dark,
    backgroundColor: theme.palette.primary.light,
  },
}));
